import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { smsApi } from "../../../api/resources";
import type { PhoneConflict } from "../../../api/types";
import { Modal } from "../../../components/Overlay";
import { Button, ErrorText, Field, StatusPill, TextInput } from "../../../components/ui";
import { fmtDate } from "../../../utils/labels";
import { DataTable, LoadState, useLoad } from "../shared";

type Action = "KEEP" | "RELEASE";

function ResolveModal({ c, onClose, onDone }: { c: PhoneConflict | null; onClose: () => void; onDone: () => void }) {
  const [action, setAction] = useState<Action>("KEEP");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!c) return;
    setBusy(true);
    setError(null);
    try {
      await smsApi.resolveConflict(c.id, action, note.trim());
      toast.success(action === "KEEP" ? "Kept with current holder" : "Released from current holder");
      setNote("");
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={!!c} onClose={onClose} title="Resolve phone conflict" testId="conflict-resolve-modal">
      {c && (
        <div className="space-y-5">
          <p className="text-sm text-zinc-300"><span className="text-white">{c.claimant_name}</span> tried to verify {c.phone_masked}, which is verified on <span className="text-white">{c.holder_name}</span>.</p>
          <div className="space-y-2">
            {([["KEEP", "Keep with current holder", "Close the conflict. The claimant must use another number."],
               ["RELEASE", "Release from current holder", "The holder becomes unverified (Registered) and is flagged. The claimant must verify again with a fresh code. Nothing transfers automatically."]] as const).map(([k, t, d]) => (
              <label key={k} data-testid={`conflict-action-${k.toLowerCase()}`} className={`block cursor-pointer rounded-sm border p-3 ${action === k ? "border-white" : "border-white/15 hover:border-white/40"}`}>
                <input type="radio" name="conflict-action" className="mr-2 accent-signal" checked={action === k} onChange={() => setAction(k)} />
                <span className="text-sm font-semibold text-white">{t}</span>
                <p className="mt-1 pl-5 text-xs text-zinc-400">{d}</p>
              </label>
            ))}
          </div>
          <Field label="Note (required)" htmlFor="conflict-note">
            <TextInput id="conflict-note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="conflict-note-input" />
          </Field>
          <ErrorText message={error} testId="conflict-resolve-error" />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose} data-testid="conflict-resolve-cancel-button">Cancel</Button>
            <Button loading={busy} disabled={note.trim().length < 3} onClick={submit} data-testid="conflict-resolve-submit-button">Resolve</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function PhoneConflictsTab({ reloadKey, onChanged }: { reloadKey: number; onChanged: () => void }) {
  const [status, setStatus] = useState("OPEN");
  const [resolving, setResolving] = useState<PhoneConflict | null>(null);
  const loader = useCallback(() => smsApi.conflicts(status || undefined), [status, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<PhoneConflict[]>(loader);
  return (
    <div data-testid="phone-conflicts-tab">
      <div className="mb-4 flex gap-2">
        {[["OPEN", "Open"], ["", "All"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`conflicts-filter-${l.toLowerCase()}`}
            className={`rounded-sm border px-3 py-1.5 text-xs uppercase tracking-wider ${status === k ? "border-white text-white" : "border-white/15 text-zinc-400"}`}>{l}</button>
        ))}
      </div>
      <LoadState error={error} loading={!data && !error} testId="conflicts-load-error" />
      {data && (
        <DataTable testId="phone-conflicts-table" headers={["Phone", "Claimant", "Current holder", "Opened", "Status", ""]} empty={data.length === 0}>
          {data.map((c) => (
            <tr key={c.id} data-testid={`conflict-row-${c.id}`} className="align-top hover:bg-white/[0.03]">
              <td className="px-5 py-4 font-mono text-xs text-zinc-300">{c.phone_masked}</td>
              <td className="px-5 py-4"><Link to={`/admin/employees/${c.claimant_employee_id}`} className="text-white hover:text-signal">{c.claimant_name}</Link></td>
              <td className="px-5 py-4">{c.holder_employee_id ? <Link to={`/admin/employees/${c.holder_employee_id}`} className="text-zinc-200 hover:text-signal">{c.holder_name}</Link> : "—"}</td>
              <td className="px-5 py-4 text-zinc-400">{fmtDate(c.created_at)}</td>
              <td className="px-5 py-4">
                <StatusPill status={c.status === "OPEN" ? "NEEDS_REVIEW" : "ACTIVE"} label={c.status} testId={`conflict-status-${c.id}`} />
                {c.resolution_note && <p className="mt-1 max-w-xs text-xs text-zinc-500">{c.resolution_note}</p>}
              </td>
              <td className="px-5 py-4 text-right">
                {c.status === "OPEN" && <Button className="h-8 px-3 text-[11px]" onClick={() => setResolving(c)} data-testid={`conflict-resolve-${c.id}`}>Resolve</Button>}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
      <ResolveModal c={resolving} onClose={() => setResolving(null)} onDone={() => { setResolving(null); onChanged(); }} />
    </div>
  );
}
